// Mental-institution lobby — a long east-west corridor with five
// subrooms branching off. Walkable footprint is roughly 64 × 28.
//
//           ┌───┐       ┌───┐
//           │ NW│       │ NE│        NW = Front Desk
//           │   │       │   │        NE = Patient Board
//           └┐ ┌┘       └┐ ┌┘
//  ┌─────────┘ └─────────┘ └────────┬───────┐
//  │                                │       │
//  │ ·········· corridor ···········│       │  east bed-room doorway
//  │                                │       │
//  └─────────┐ ┌─────────┐ ┌────────┴───────┘
//           ┌┘ └┐       ┌┘ └┐
//           │ SW│       │ SE│        SW = Soda Machine
//           │   │       │   │        SE = Commissary
//           └───┘       └───┘
//
// Doorways are simple 8-unit gaps in the corridor wall; each subroom
// has three outer walls sealing the pocket. Wall colliders overlap at
// corners so the player can't slip through a seam. Outside the wing,
// street benches and flowers dress the grass as a decorative courtyard.

import * as THREE from 'three';
import type { AABB } from '../scene/world';

export type PlazaKind = 'matchmaker' | 'shop' | 'leaderboards' | 'gacha';

export type PlazaStructure = {
  kind: PlazaKind;
  label: string;
  x: number;
  z: number;
  group: THREE.Group;
  glowRing: THREE.Mesh;
  interactRadius: number;
  update: (tSec: number) => void;
};

const INTERACT_RADIUS = 3.2;

// --- Geometry (world units) ---
// Corridor — the always-walkable backbone of the lobby.
const CORR_HALF_X = 22;
const CORR_HALF_Z = 6;

// Subrooms — each is 8 wide × 8 deep, branching north or south.
const SUB_HALF_W = 4;
const SUB_DEPTH = 8;
// Subroom centres — chosen so NW/NE sit symmetrically about x=0,
// with enough corridor wall left on both sides that the room reads
// as a deliberate pocket, not an open side.
const SUB_CX_INNER = 13;     // NW at x=-13, NE at x=+13

// Bed room at the east end of the corridor.
const BED_WEST_X = CORR_HALF_X;            // shares corridor's east wall
const BED_EAST_X = CORR_HALF_X + 10;       // 10u deep
const BED_HALF_Z = CORR_HALF_Z;            // same z extent as corridor
const BED_CENTRE_X = (BED_WEST_X + BED_EAST_X) / 2;

// Wall appearance.
const WALL_H = 4.2;
const WALL_THICK = 0.5;
// Split point between the "base" wall band (always writes depth so it
// occludes the player's feet and body) and the "crown" band (depth
// toggled per-frame so it doesn't clip the player's head when the
// player is standing on the far side of the wall). 2.0u ≈ top of
// player sprite so the whole body is occluded when the wall is in
// front of the camera.
const WALL_BASE_H = 2.0;

// Fixture positions — inside each subroom, a bit off the back wall so
// the player can circle them.
const FIXTURE_POS: Record<PlazaKind, { x: number; z: number }> = {
  matchmaker:   { x: -SUB_CX_INNER, z: -(CORR_HALF_Z + SUB_DEPTH - 3) }, // NW
  leaderboards: { x:  SUB_CX_INNER, z: -(CORR_HALF_Z + SUB_DEPTH - 3) }, // NE
  gacha:        { x: -SUB_CX_INNER, z:  (CORR_HALF_Z + SUB_DEPTH - 3) }, // SW
  shop:         { x:  SUB_CX_INNER, z:  (CORR_HALF_Z + SUB_DEPTH - 3) }, // SE
};

// Palette — sterile institutional.
const COL_WHITE = 0xf3f4ec;
const COL_VINYL = 0xe4dfcf;
const COL_TEAL  = 0x4dd4c6;
const COL_STEEL = 0x9ca3af;
const COL_RED   = 0xef4444;

export function buildPlazaStructures(
  parent: THREE.Object3D,
  colliders: AABB[] = [],
): PlazaStructure[] {
  buildWing(parent, colliders);
  buildOutdoorDressing(parent);
  return [
    makeFrontDesk(parent,    FIXTURE_POS.matchmaker),
    makePatientBoard(parent, FIXTURE_POS.leaderboards),
    makeSodaMachine(parent,  FIXTURE_POS.gacha),
    makeMedsCart(parent,     FIXTURE_POS.shop),
  ];
}

// -------------------------------------------------------------------
//   Wall helper — visible box + collider. The collider extends
//   WALL_THICK / 2 along its long axis so corner AABBs interpenetrate.
// -------------------------------------------------------------------

function addWall(
  parent: THREE.Object3D, colliders: AABB[],
  cx: number, cz: number, w: number, d: number,
): void {
  const wallMat = new THREE.MeshStandardMaterial({
    color: COL_WHITE,
    emissive: 0x2a3a3a,
    emissiveIntensity: 0.08,
    roughness: 0.7,
  });
  const trimMat = new THREE.MeshStandardMaterial({
    color: COL_TEAL,
    emissive: COL_TEAL,
    emissiveIntensity: 0.55,
  });
  // Split into two stacked boxes. The BASE band (y=0..WALL_BASE_H)
  // always writes depth so the player sprite is visually blocked by
  // the wall's south face — matches how buildings occlude sprites from
  // below. The CROWN band (y=WALL_BASE_H..WALL_H) toggles depthWrite
  // based on the wall's side relative to the player, so the player's
  // head isn't clipped when standing south of a north-corridor wall.
  const base = new THREE.Mesh(
    new THREE.BoxGeometry(w, WALL_BASE_H, d), wallMat.clone(),
  );
  base.position.set(cx, WALL_BASE_H / 2, cz);
  base.userData.lobbyWall = true;
  base.userData.wallZ = cz;
  parent.add(base);
  const crown = new THREE.Mesh(
    new THREE.BoxGeometry(w, WALL_H - WALL_BASE_H, d), wallMat.clone(),
  );
  crown.position.set(cx, WALL_BASE_H + (WALL_H - WALL_BASE_H) / 2, cz);
  crown.userData.lobbyWall = true;
  crown.userData.wallZ = cz;
  parent.add(crown);
  const trim = new THREE.Mesh(
    new THREE.BoxGeometry(w + 0.05, 0.1, d + 0.05),
    trimMat.clone(),
  );
  trim.position.set(cx, 1.5, cz);
  trim.userData.lobbyWall = true;
  trim.userData.wallZ = cz;
  parent.add(trim);
  const horizontal = w > d;
  const extX = horizontal ? WALL_THICK / 2 : 0;
  const extZ = horizontal ? 0 : WALL_THICK / 2;
  colliders.push({
    min: new THREE.Vector3(cx - w / 2 - extX - 0.05, 0, cz - d / 2 - extZ - 0.05),
    max: new THREE.Vector3(cx + w / 2 + extX + 0.05, WALL_H, cz + d / 2 + extZ + 0.05),
  });
}

// Polygon-approximation of the intended walkable area. Called by the
// escape-check flood fill to classify reachable points. Generous 0.05
// fudge so doorway edges don't falsely register as out-of-bounds.
//
// Zones (all inclusive of walls/doorway gaps):
//   • Corridor rect:  x ∈ [-22, 22], z ∈ [-6, 6]
//   • 4 subrooms:     (±13 ± 4) × (z-side of corridor, ±8 deep)
//   • Bed room:       x ∈ [22, 32], z ∈ [-6, 6]
export function isLobbyWalkable(x: number, z: number): boolean {
  const PAD = 0.05;
  // Corridor
  if (x >= -CORR_HALF_X - PAD && x <= CORR_HALF_X + PAD &&
      z >= -CORR_HALF_Z - PAD && z <= CORR_HALF_Z + PAD) return true;
  // Bed room
  if (x >= BED_WEST_X - PAD && x <= BED_EAST_X + PAD &&
      z >= -BED_HALF_Z - PAD && z <= BED_HALF_Z + PAD) return true;
  // Subrooms
  for (const cx of [-SUB_CX_INNER, SUB_CX_INNER]) {
    for (const cz of [-(CORR_HALF_Z + SUB_DEPTH / 2), (CORR_HALF_Z + SUB_DEPTH / 2)]) {
      if (x >= cx - SUB_HALF_W - PAD && x <= cx + SUB_HALF_W + PAD &&
          z >= cz - SUB_DEPTH / 2 - PAD && z <= cz + SUB_DEPTH / 2 + PAD) return true;
    }
  }
  return false;
}

export type LobbyCheckReport = {
  escapes: Array<{ x: number; z: number }>;
  intrusions: Array<{ x: number; z: number }>;
  reachedCells: number;
  durationMs: number;
};

// Flood-fills the walkable grid from the corridor centre and classifies
// every reachable cell. Two failure signals:
//   • escapes    — cell reached by flood that lies outside the intended
//                  walkable polygon (= player can slip through a seam).
//   • intrusions — in-polygon cell that sits inside a wall collider (=
//                  wall clips into walkable space).
// Colliders are inflated by the player half-width (0.25) so the check
// mirrors what the physics pass actually permits.
export function checkLobbyEscapes(colliders: ReadonlyArray<AABB>): LobbyCheckReport {
  const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  const STEP = 0.25;
  const PAD = 0.25; // player half-width
  const MINX = -40, MAXX = 45;
  const MINZ = -20, MAXZ = 20;
  const width  = Math.round((MAXX - MINX) / STEP) + 1;
  const height = Math.round((MAXZ - MINZ) / STEP) + 1;
  const seen = new Uint8Array(width * height);
  const key = (ix: number, iz: number) => ix * height + iz;
  const blocked = (x: number, z: number): boolean => {
    for (const a of colliders) {
      if (x >= a.min.x - PAD && x <= a.max.x + PAD &&
          z >= a.min.z - PAD && z <= a.max.z + PAD) return true;
    }
    return false;
  };
  const stack: Array<[number, number]> = [];
  const six = Math.round((0 - MINX) / STEP);
  const siz = Math.round((0 - MINZ) / STEP);
  stack.push([six, siz]);
  seen[key(six, siz)] = 1;
  const escapes: Array<{ x: number; z: number }> = [];
  const intrusions: Array<{ x: number; z: number }> = [];
  let reached = 0;
  while (stack.length) {
    const cell = stack.pop()!;
    const ix = cell[0]; const iz = cell[1];
    reached++;
    const cx = MINX + ix * STEP;
    const cz = MINZ + iz * STEP;
    if (!isLobbyWalkable(cx, cz)) escapes.push({ x: cx, z: cz });
    if (blocked(cx, cz) && isLobbyWalkable(cx, cz)) intrusions.push({ x: cx, z: cz });
    for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
      const nix = ix + dx; const niz = iz + dz;
      if (nix < 0 || nix >= width || niz < 0 || niz >= height) continue;
      const k = key(nix, niz);
      if (seen[k]) continue;
      const nx = MINX + nix * STEP;
      const nz = MINZ + niz * STEP;
      if (blocked(nx, nz)) continue;
      seen[k] = 1;
      stack.push([nix, niz]);
    }
  }
  const durationMs = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0;
  return { escapes, intrusions, reachedCells: reached, durationMs };
}

// Called per-frame from main.ts while in the lobby. Walls flagged with
// `lobbyWall` switch between two render regimes based on which side of
// the player they sit on:
//   • South of player (wallZ > playerZ): normal opaque draw — wall
//     writes depth, occludes sprite as expected. Matches building
//     occlusion from the south so the wall "blocks the path from the
//     bottom" visually.
//   • North of player (wallZ < playerZ): force-behind draw — depthTest
//     off + renderOrder negative so the wall always renders before and
//     below the player sprite. Keeps the sprite from ever being clipped
//     by a wall that's meant to be behind it.
export function updateLobbyWallOcclusion(root: THREE.Object3D, playerZ: number): void {
  root.traverse((obj) => {
    if (!obj.userData.lobbyWall) return;
    const mesh = obj as THREE.Mesh;
    const mat = mesh.material as THREE.MeshStandardMaterial | undefined;
    if (!mat || Array.isArray(mat)) return;
    const wallZ = obj.userData.wallZ as number;
    const isSouth = wallZ > playerZ;
    if (isSouth) {
      mat.transparent = false;
      mat.depthWrite = true;
      mat.depthTest = true;
      mesh.renderOrder = 0;
    } else {
      // North of player — push behind everything so the sprite always
      // renders on top, even when wall top pixels would otherwise share
      // screen space with the sprite's head.
      mat.transparent = true;
      mat.depthWrite = false;
      mat.depthTest = false;
      mesh.renderOrder = -5;
    }
    mat.needsUpdate = true;
  });
}

// -------------------------------------------------------------------
//   Wing shell
// -------------------------------------------------------------------

function buildWing(parent: THREE.Object3D, colliders: AABB[]): void {
  const floorMat = new THREE.MeshStandardMaterial({
    color: COL_VINYL, roughness: 0.85,
  });

  // ---- Floors (one per walkable zone) ----
  // Corridor
  const corridorFloor = new THREE.Mesh(
    new THREE.PlaneGeometry(CORR_HALF_X * 2, CORR_HALF_Z * 2),
    floorMat,
  );
  corridorFloor.rotation.x = -Math.PI / 2;
  corridorFloor.position.set(0, 0.02, 0);
  parent.add(corridorFloor);
  // Four subroom floors + bed-room floor
  const checkerTex = makeCheckerTexture(1024, 0xe5e7eb, 0xb9bdc5, 16);
  const subFloorMat = new THREE.MeshStandardMaterial({ map: checkerTex, roughness: 0.8 });
  for (const cx of [-SUB_CX_INNER, SUB_CX_INNER]) {
    for (const cz of [-(CORR_HALF_Z + SUB_DEPTH / 2), (CORR_HALF_Z + SUB_DEPTH / 2)]) {
      const f = new THREE.Mesh(
        new THREE.PlaneGeometry(SUB_HALF_W * 2, SUB_DEPTH),
        subFloorMat,
      );
      f.rotation.x = -Math.PI / 2;
      f.position.set(cx, 0.03, cz);
      parent.add(f);
    }
  }
  // Bed-room floor
  const bedFloor = new THREE.Mesh(
    new THREE.PlaneGeometry(BED_EAST_X - BED_WEST_X, BED_HALF_Z * 2),
    floorMat,
  );
  bedFloor.rotation.x = -Math.PI / 2;
  bedFloor.position.set(BED_CENTRE_X, 0.02, 0);
  parent.add(bedFloor);

  // ---- Red emergency stripe along the corridor centre ----
  const redMat = new THREE.MeshBasicMaterial({ color: COL_RED });
  const corridorStripe = new THREE.Mesh(
    new THREE.PlaneGeometry(CORR_HALF_X * 2 + (BED_EAST_X - BED_WEST_X), 0.25),
    redMat,
  );
  corridorStripe.rotation.x = -Math.PI / 2;
  corridorStripe.position.set((BED_EAST_X - CORR_HALF_X) / 2, 0.06, 0);
  parent.add(corridorStripe);

  // ---- Corridor walls (gaps for subroom doorways + east bed-room door) ----
  // North corridor wall at z = -CORR_HALF_Z: gaps at x ∈ ±(SUB_CX_INNER ± SUB_HALF_W)
  // Solid segments: far-west, between-NW-NE, far-east.
  const nwGapLo = -SUB_CX_INNER - SUB_HALF_W;
  const nwGapHi = -SUB_CX_INNER + SUB_HALF_W;
  const neGapLo =  SUB_CX_INNER - SUB_HALF_W;
  const neGapHi =  SUB_CX_INNER + SUB_HALF_W;
  // far-west: x ∈ [-CORR_HALF_X, nwGapLo]
  addWall(parent, colliders,
    (-CORR_HALF_X + nwGapLo) / 2, -CORR_HALF_Z,
    nwGapLo - (-CORR_HALF_X), WALL_THICK,
  );
  // between: x ∈ [nwGapHi, neGapLo]
  addWall(parent, colliders,
    (nwGapHi + neGapLo) / 2, -CORR_HALF_Z,
    neGapLo - nwGapHi, WALL_THICK,
  );
  // far-east: x ∈ [neGapHi, CORR_HALF_X]
  addWall(parent, colliders,
    (neGapHi + CORR_HALF_X) / 2, -CORR_HALF_Z,
    CORR_HALF_X - neGapHi, WALL_THICK,
  );
  // South corridor wall — mirror of north, at z = +CORR_HALF_Z, but
  // with an additional ENTRANCE DOORWAY cut in the middle segment so
  // players can walk from the garden / portal / pavement strip back
  // into the corridor. Far-west + far-east segments are unchanged;
  // middle segment splits into two halves flanking a 4u gap at x=0.
  addWall(parent, colliders,
    (-CORR_HALF_X + nwGapLo) / 2, CORR_HALF_Z,
    nwGapLo - (-CORR_HALF_X), WALL_THICK,
  );
  const SOUTH_DOOR_HALF = 2; // 4u total doorway, x ∈ [-2, +2]
  // Middle-left: x ∈ [nwGapHi, -SOUTH_DOOR_HALF]
  addWall(parent, colliders,
    (nwGapHi + -SOUTH_DOOR_HALF) / 2, CORR_HALF_Z,
    -SOUTH_DOOR_HALF - nwGapHi, WALL_THICK,
  );
  // Middle-right: x ∈ [+SOUTH_DOOR_HALF, neGapLo]
  addWall(parent, colliders,
    (SOUTH_DOOR_HALF + neGapLo) / 2, CORR_HALF_Z,
    neGapLo - SOUTH_DOOR_HALF, WALL_THICK,
  );
  addWall(parent, colliders,
    (neGapHi + CORR_HALF_X) / 2, CORR_HALF_Z,
    CORR_HALF_X - neGapHi, WALL_THICK,
  );
  // Door frame — two posts + lintel at the doorway so the gap reads
  // as "entrance" rather than "missing wall". Teal accent matches the
  // rest of the institutional palette.
  const frameMat = new THREE.MeshStandardMaterial({
    color: COL_WHITE, emissive: COL_TEAL, emissiveIntensity: 0.22, roughness: 0.6,
  });
  const postL = new THREE.Mesh(
    new THREE.BoxGeometry(WALL_THICK, 2.6, WALL_THICK),
    frameMat,
  );
  postL.position.set(-SOUTH_DOOR_HALF, 1.3, CORR_HALF_Z);
  parent.add(postL);
  const postR = new THREE.Mesh(
    new THREE.BoxGeometry(WALL_THICK, 2.6, WALL_THICK),
    frameMat,
  );
  postR.position.set(SOUTH_DOOR_HALF, 1.3, CORR_HALF_Z);
  parent.add(postR);
  const lintel = new THREE.Mesh(
    new THREE.BoxGeometry(SOUTH_DOOR_HALF * 2 + WALL_THICK * 2, 0.7, WALL_THICK),
    frameMat,
  );
  lintel.position.set(0, 2.95, CORR_HALF_Z);
  parent.add(lintel);
  // ENTRANCE sprite label hanging above the doorway on the outdoor
  // (south) side — so someone walking in from the garden sees it.
  {
    const c = document.createElement('canvas');
    c.width = 384; c.height = 96;
    const ctx = c.getContext('2d')!;
    ctx.fillStyle = 'rgba(15,23,42,.92)';
    ctx.fillRect(10, 10, c.width - 20, c.height - 20);
    ctx.strokeStyle = '#4dd4c6';
    ctx.lineWidth = 3;
    ctx.strokeRect(10, 10, c.width - 20, c.height - 20);
    ctx.fillStyle = '#4dd4c6';
    ctx.font = '900 42px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = '#4dd4c6';
    ctx.shadowBlur = 10;
    ctx.fillText('ENTRANCE', c.width / 2, c.height / 2 + 2);
    const tex = new THREE.CanvasTexture(c);
    tex.needsUpdate = true;
    const sign = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false }));
    sign.scale.set(3.0, 0.75, 1);
    sign.position.set(0, 3.8, CORR_HALF_Z + 0.6);
    parent.add(sign);
  }

  // West corridor end wall — fully sealed
  addWall(parent, colliders,
    -CORR_HALF_X, 0, WALL_THICK, CORR_HALF_Z * 2 + WALL_THICK,
  );
  // East corridor wall — two segments flanking the bed-room doorway.
  // Bed doorway at z ∈ [-3, +3].
  const BED_DOOR_HALF = 3;
  addWall(parent, colliders,
    CORR_HALF_X, (-CORR_HALF_Z - BED_DOOR_HALF) / 2,
    WALL_THICK, CORR_HALF_Z - BED_DOOR_HALF + WALL_THICK / 2,
  );
  addWall(parent, colliders,
    CORR_HALF_X, (CORR_HALF_Z + BED_DOOR_HALF) / 2,
    WALL_THICK, CORR_HALF_Z - BED_DOOR_HALF + WALL_THICK / 2,
  );

  // ---- Subroom walls (3 outer walls per subroom) ----
  // NW subroom: x ∈ [-SUB_CX_INNER-SUB_HALF_W, -SUB_CX_INNER+SUB_HALF_W]
  //             z ∈ [-CORR_HALF_Z-SUB_DEPTH, -CORR_HALF_Z]
  buildSubroom(parent, colliders, -SUB_CX_INNER, -(CORR_HALF_Z + SUB_DEPTH / 2));
  buildSubroom(parent, colliders,  SUB_CX_INNER, -(CORR_HALF_Z + SUB_DEPTH / 2));
  buildSubroom(parent, colliders, -SUB_CX_INNER,  (CORR_HALF_Z + SUB_DEPTH / 2));
  buildSubroom(parent, colliders,  SUB_CX_INNER,  (CORR_HALF_Z + SUB_DEPTH / 2));

  // ---- Bed room walls ----
  // North, south, east (west side shares corridor east wall with doorway).
  addWall(parent, colliders,
    BED_CENTRE_X, -BED_HALF_Z, BED_EAST_X - BED_WEST_X + WALL_THICK, WALL_THICK,
  );
  addWall(parent, colliders,
    BED_CENTRE_X,  BED_HALF_Z, BED_EAST_X - BED_WEST_X + WALL_THICK, WALL_THICK,
  );
  addWall(parent, colliders,
    BED_EAST_X, 0, WALL_THICK, BED_HALF_Z * 2 + WALL_THICK,
  );

  // ---- Ceiling strips ----
  const lightMat = new THREE.MeshBasicMaterial({
    color: 0xeafff7, transparent: true, opacity: 0.95,
  });
  for (let i = -1; i <= 1; i++) {
    const strip = new THREE.Mesh(
      new THREE.BoxGeometry(CORR_HALF_X * 1.6, 0.12, 0.6),
      lightMat,
    );
    strip.position.set(0, WALL_H - 0.1, i * 3);
    parent.add(strip);
  }
  // One strip in each subroom + bed room
  for (const cx of [-SUB_CX_INNER, SUB_CX_INNER]) {
    for (const cz of [-(CORR_HALF_Z + SUB_DEPTH / 2), (CORR_HALF_Z + SUB_DEPTH / 2)]) {
      const strip = new THREE.Mesh(
        new THREE.BoxGeometry(SUB_HALF_W * 1.4, 0.12, 0.6),
        lightMat,
      );
      strip.position.set(cx, WALL_H - 0.1, cz);
      parent.add(strip);
    }
  }
  const bedStrip = new THREE.Mesh(
    new THREE.BoxGeometry((BED_EAST_X - BED_WEST_X) * 0.7, 0.12, 0.6),
    lightMat,
  );
  bedStrip.position.set(BED_CENTRE_X, WALL_H - 0.1, 0);
  parent.add(bedStrip);

  // ---- Door labels ----
  // Subroom back-walls — label faces the corridor.
  addDoorLabel(parent, -SUB_CX_INNER, -(CORR_HALF_Z + SUB_DEPTH) + 0.05, 'ADMISSIONS',   0x4dd4c6, 0);
  addDoorLabel(parent,  SUB_CX_INNER, -(CORR_HALF_Z + SUB_DEPTH) + 0.05, 'PATIENT WARD', 0xfbbf24, 0);
  addDoorLabel(parent, -SUB_CX_INNER,  (CORR_HALF_Z + SUB_DEPTH) - 0.05, 'VENDING',      0xf59e0b, Math.PI);
  addDoorLabel(parent,  SUB_CX_INNER,  (CORR_HALF_Z + SUB_DEPTH) - 0.05, 'COMMISSARY',   0xf472b6, Math.PI);
  // Bed-room east wall
  addDoorLabel(parent, BED_EAST_X - 0.05, 0, '🛏 YOUR BED', 0x60a5fa, -Math.PI / 2);

  // ---- Waiting-room chairs flanking each subroom doorway (in corridor) ----
  for (const [cx, cz, yaw] of [
    [-SUB_CX_INNER - 3.5, -CORR_HALF_Z + 1.2, 0],
    [-SUB_CX_INNER + 3.5, -CORR_HALF_Z + 1.2, 0],
    [ SUB_CX_INNER - 3.5, -CORR_HALF_Z + 1.2, 0],
    [ SUB_CX_INNER + 3.5, -CORR_HALF_Z + 1.2, 0],
    [-SUB_CX_INNER - 3.5,  CORR_HALF_Z - 1.2, Math.PI],
    [-SUB_CX_INNER + 3.5,  CORR_HALF_Z - 1.2, Math.PI],
    [ SUB_CX_INNER - 3.5,  CORR_HALF_Z - 1.2, Math.PI],
    [ SUB_CX_INNER + 3.5,  CORR_HALF_Z - 1.2, Math.PI],
  ] as const) {
    addChair(parent, cx, cz, yaw);
  }

  // ---- Potted plants at corridor ends ----
  for (const [x, z] of [
    [-CORR_HALF_X + 1.5, -CORR_HALF_Z + 1.5],
    [-CORR_HALF_X + 1.5,  CORR_HALF_Z - 1.5],
  ] as const) {
    addPlant(parent, x, z);
  }
}

function buildSubroom(
  parent: THREE.Object3D, colliders: AABB[],
  cx: number, cz: number,
): void {
  // Subroom spans (cx ± SUB_HALF_W) × (cz ± SUB_DEPTH / 2). The
  // corridor side (facing origin) has a doorway — that side's wall is
  // handled by the corridor wall segments above. We only build the
  // three outer walls here.
  const north = cz < 0; // subroom is north of corridor?
  // Back wall (far side from corridor)
  const backZ = cz + (north ? -SUB_DEPTH / 2 : SUB_DEPTH / 2);
  addWall(parent, colliders,
    cx, backZ, SUB_HALF_W * 2 + WALL_THICK, WALL_THICK,
  );
  // West wall
  addWall(parent, colliders,
    cx - SUB_HALF_W, cz, WALL_THICK, SUB_DEPTH + WALL_THICK,
  );
  // East wall
  addWall(parent, colliders,
    cx + SUB_HALF_W, cz, WALL_THICK, SUB_DEPTH + WALL_THICK,
  );
}

function makeCheckerTexture(size: number, a: number, b: number, tiles: number): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = size; c.height = size;
  const ctx = c.getContext('2d')!;
  const t = size / tiles;
  const ca = '#' + a.toString(16).padStart(6, '0');
  const cb = '#' + b.toString(16).padStart(6, '0');
  for (let y = 0; y < tiles; y++) {
    for (let x = 0; x < tiles; x++) {
      ctx.fillStyle = (x + y) % 2 === 0 ? ca : cb;
      ctx.fillRect(x * t, y * t, t, t);
    }
  }
  const tex = new THREE.CanvasTexture(c);
  tex.needsUpdate = true;
  return tex;
}

function addDoorLabel(
  parent: THREE.Object3D,
  x: number, z: number, text: string, color: number, rotY: number,
): void {
  const c = document.createElement('canvas');
  c.width = 512; c.height = 128;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#0f172a';
  ctx.fillRect(0, 0, c.width, c.height);
  ctx.strokeStyle = '#' + color.toString(16).padStart(6, '0');
  ctx.lineWidth = 8;
  ctx.strokeRect(8, 8, c.width - 16, c.height - 16);
  ctx.fillStyle = '#f3f4ec';
  ctx.font = '900 54px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, c.width / 2, c.height / 2);
  const tex = new THREE.CanvasTexture(c);
  tex.needsUpdate = true;
  const mat = new THREE.MeshBasicMaterial({ map: tex, transparent: true });
  const panel = new THREE.Mesh(new THREE.PlaneGeometry(3.2, 0.8), mat);
  panel.position.set(x, 3.0, z);
  panel.rotation.y = rotY;
  parent.add(panel);
}

function addChair(parent: THREE.Object3D, x: number, z: number, yaw: number): void {
  const group = new THREE.Group();
  group.position.set(x, 0, z);
  group.rotation.y = yaw;
  const seatMat = new THREE.MeshStandardMaterial({
    color: 0x60a5fa, emissive: 0x1e3a8a, emissiveIntensity: 0.2, roughness: 0.7,
  });
  const legMat = new THREE.MeshStandardMaterial({
    color: 0x4b5563, metalness: 0.7, roughness: 0.3,
  });
  const seat = new THREE.Mesh(new THREE.BoxGeometry(1.0, 0.12, 1.0), seatMat);
  seat.position.y = 0.55;
  group.add(seat);
  const back = new THREE.Mesh(new THREE.BoxGeometry(1.0, 1.1, 0.12), seatMat);
  back.position.set(0, 1.1, -0.45);
  group.add(back);
  for (const [lx, lz] of [[-0.45, -0.45], [0.45, -0.45], [-0.45, 0.45], [0.45, 0.45]] as const) {
    const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 0.55, 6), legMat);
    leg.position.set(lx, 0.28, lz);
    group.add(leg);
  }
  parent.add(group);
}

function addPlant(parent: THREE.Object3D, x: number, z: number): void {
  const potMat = new THREE.MeshStandardMaterial({
    color: 0x6b7280, roughness: 0.6,
  });
  const pot = new THREE.Mesh(new THREE.CylinderGeometry(0.4, 0.35, 0.55, 10), potMat);
  pot.position.set(x, 0.28, z);
  parent.add(pot);
  const leafMat = new THREE.MeshStandardMaterial({
    color: 0x16a34a, roughness: 0.55, emissive: 0x15803d, emissiveIntensity: 0.1,
  });
  const leaves = new THREE.Mesh(new THREE.IcosahedronGeometry(0.6, 0), leafMat);
  leaves.position.set(x, 1.0, z);
  parent.add(leaves);
  const top = new THREE.Mesh(new THREE.IcosahedronGeometry(0.35, 0), leafMat);
  top.position.set(x, 1.5, z);
  parent.add(top);
}

// -------------------------------------------------------------------
//   Outdoor dressing — street benches + flower patches on the grass
//   outside the lobby walls. Purely decorative; no colliders so they
//   can be placed anywhere (they're unreachable since lobby walls
//   enclose the player).
// -------------------------------------------------------------------

function buildOutdoorDressing(parent: THREE.Object3D): void {
  // Solid grass backdrop — replaces the match-only world ground plane
  // (which has roads baked into its shader). Kept flat green so the
  // outdoor area reads as a garden, not a city block.
  const grass = new THREE.Mesh(
    new THREE.PlaneGeometry(200, 200),
    new THREE.MeshStandardMaterial({
      color: 0x2d6a3a,
      emissive: 0x14301c,
      emissiveIntensity: 0.15,
      roughness: 0.95,
    }),
  );
  grass.rotation.x = -Math.PI / 2;
  grass.position.y = 0.01; // just below the lobby floor so it never z-fights
  parent.add(grass);

  // Single pavement strip south of the spawn — the only "sidewalk".
  const pavement = new THREE.Mesh(
    new THREE.BoxGeometry(40, 0.06, 4),
    new THREE.MeshStandardMaterial({
      color: 0xbfbfb8, roughness: 0.9,
    }),
  );
  pavement.position.set(0, 0.05, 20);
  parent.add(pavement);

  // Benches framing the wing on the N, S, and W sides. East side has
  // the bed room, skip that side. Each bench faces the wall behind it.
  const benchSpecs: Array<{ x: number; z: number; yaw: number }> = [
    // South side, sitting on the pavement strip
    { x: -14, z: 20, yaw: 0 },
    { x:   0, z: 20, yaw: 0 },
    { x:  14, z: 20, yaw: 0 },
    // North side
    { x: -18, z: -22, yaw: Math.PI },
    { x:   0, z: -22, yaw: Math.PI },
    { x:  18, z: -22, yaw: Math.PI },
    // West side
    { x: -32, z: -6, yaw: -Math.PI / 2 },
    { x: -32, z:  6, yaw: -Math.PI / 2 },
  ];
  for (const b of benchSpecs) addBench(parent, b.x, b.z, b.yaw);

  // Scattered flower patches — deterministic positions so the patches
  // read as a planned garden rather than random litter. All off the
  // pavement strip so the sidewalk stays clean.
  const flowerSpots: Array<[number, number]> = [
    // South side flowers behind and flanking the benches
    [-24, 24], [ -6, 24], [  6, 24], [ 24, 24],
    // North side
    [-24, -20], [-12, -24], [ 12, -24], [ 24, -20],
    [-18, -19], [  0, -19], [ 18, -19],
    // West side
    [-34,  -2], [-34,   2], [-34, -14], [-34, 14],
  ];
  for (const [x, z] of flowerSpots) addFlowerPatch(parent, x, z);
}

function addBench(parent: THREE.Object3D, x: number, z: number, yaw: number): void {
  const g = new THREE.Group();
  g.position.set(x, 0, z);
  g.rotation.y = yaw;
  // Slats
  const slatMat = new THREE.MeshStandardMaterial({
    color: 0x78350f, roughness: 0.75, emissive: 0x3f2108, emissiveIntensity: 0.08,
  });
  const seat = new THREE.Mesh(new THREE.BoxGeometry(2.8, 0.12, 0.7), slatMat);
  seat.position.y = 0.55;
  g.add(seat);
  const back = new THREE.Mesh(new THREE.BoxGeometry(2.8, 0.7, 0.12), slatMat);
  back.position.set(0, 0.95, -0.3);
  g.add(back);
  // Cast-iron legs
  const legMat = new THREE.MeshStandardMaterial({
    color: 0x1f2937, metalness: 0.6, roughness: 0.4,
  });
  for (const lx of [-1.2, 1.2]) {
    const leg = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.55, 0.7), legMat);
    leg.position.set(lx, 0.28, 0);
    g.add(leg);
  }
  parent.add(g);
}

function addFlowerPatch(parent: THREE.Object3D, x: number, z: number): void {
  // Patch base — a small green disc of dense grass
  const base = new THREE.Mesh(
    new THREE.CircleGeometry(0.9, 12),
    new THREE.MeshStandardMaterial({
      color: 0x15803d, roughness: 0.85, emissive: 0x166534, emissiveIntensity: 0.15,
    }),
  );
  base.rotation.x = -Math.PI / 2;
  base.position.set(x, 0.04, z);
  parent.add(base);
  // Five little flowers per patch with randomized pastel colors
  const petalColors = [0xfbbf24, 0xf472b6, 0xe0e7ff, 0xfca5a5, 0xa7f3d0];
  for (let i = 0; i < 5; i++) {
    const angle = (i / 5) * Math.PI * 2;
    const r = 0.35 + Math.random() * 0.3;
    const fx = x + Math.cos(angle) * r;
    const fz = z + Math.sin(angle) * r;
    // Stem
    const stem = new THREE.Mesh(
      new THREE.CylinderGeometry(0.03, 0.03, 0.35, 6),
      new THREE.MeshStandardMaterial({ color: 0x166534 }),
    );
    stem.position.set(fx, 0.2, fz);
    parent.add(stem);
    // Bloom — a small emissive sphere
    const petalColor = petalColors[i % petalColors.length];
    const bloom = new THREE.Mesh(
      new THREE.SphereGeometry(0.12, 6, 5),
      new THREE.MeshStandardMaterial({
        color: petalColor,
        emissive: petalColor,
        emissiveIntensity: 0.5,
      }),
    );
    bloom.position.set(fx, 0.42, fz);
    parent.add(bloom);
  }
}

// -------------------------------------------------------------------
//   Fixture helpers (shared)
// -------------------------------------------------------------------

function makeGlowRing(color: number): THREE.Mesh {
  const mat = new THREE.MeshBasicMaterial({
    color, transparent: true, opacity: 0.45,
    side: THREE.DoubleSide, depthWrite: false,
  });
  const mesh = new THREE.Mesh(new THREE.RingGeometry(2.0, 2.8, 32), mat);
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.y = 0.15;
  return mesh;
}

function makeTextPanel(text: string, w: number, h: number, color = '#0f172a', bg = '#f3f4ec'): THREE.Mesh {
  const c = document.createElement('canvas');
  c.width = 512; c.height = 128;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, c.width, c.height);
  ctx.fillStyle = color;
  ctx.font = '800 62px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, c.width / 2, c.height / 2);
  const tex = new THREE.CanvasTexture(c);
  tex.needsUpdate = true;
  const mat = new THREE.MeshBasicMaterial({
    map: tex, transparent: true, side: THREE.DoubleSide, depthWrite: false,
  });
  return new THREE.Mesh(new THREE.PlaneGeometry(w, h), mat);
}

// -------------------------------------------------------------------
//   Fixtures
// -------------------------------------------------------------------

function makeFrontDesk(parent: THREE.Object3D, pos: { x: number; z: number }): PlazaStructure {
  const g = new THREE.Group();
  g.position.set(pos.x, 0, pos.z);
  // NW subroom — face south (toward corridor doorway)
  g.rotation.y = 0;

  const body = new THREE.Mesh(
    new THREE.BoxGeometry(4.4, 1.1, 1.4),
    new THREE.MeshStandardMaterial({
      color: COL_WHITE, roughness: 0.55,
      emissive: 0x0f172a, emissiveIntensity: 0.05,
    }),
  );
  body.position.y = 0.55;
  g.add(body);
  const top = new THREE.Mesh(
    new THREE.BoxGeometry(4.6, 0.08, 1.55),
    new THREE.MeshStandardMaterial({
      color: COL_TEAL, emissive: COL_TEAL, emissiveIntensity: 0.4,
    }),
  );
  top.position.y = 1.14;
  g.add(top);
  const monitor = new THREE.Mesh(
    new THREE.BoxGeometry(1.0, 0.8, 0.08),
    new THREE.MeshStandardMaterial({
      color: 0x0f172a, emissive: 0x22c55e, emissiveIntensity: 1.2,
    }),
  );
  monitor.position.set(-1.0, 1.85, -0.4);
  g.add(monitor);
  const bell = new THREE.Mesh(
    new THREE.SphereGeometry(0.2, 12, 10),
    new THREE.MeshStandardMaterial({
      color: COL_STEEL, roughness: 0.15, metalness: 0.8,
    }),
  );
  bell.position.set(1.4, 1.3, 0.3);
  g.add(bell);
  const sign = makeTextPanel('CHECK IN', 3.4, 0.8, '#0f172a', '#4dd4c6');
  sign.position.set(0, 2.7, 0);
  g.add(sign);
  const ring = makeGlowRing(0x4dd4c6);
  g.add(ring);

  parent.add(g);
  return {
    kind: 'matchmaker', label: 'Front Desk',
    x: pos.x, z: pos.z, group: g, glowRing: ring,
    interactRadius: INTERACT_RADIUS,
    update(tSec) {
      (monitor.material as THREE.MeshStandardMaterial).emissiveIntensity =
        0.9 + 0.5 * (0.5 + 0.5 * Math.sin(tSec * 4));
      (ring.material as THREE.MeshBasicMaterial).opacity =
        0.35 + 0.22 * (0.5 + 0.5 * Math.sin(tSec * 2));
    },
  };
}

function makeSodaMachine(parent: THREE.Object3D, pos: { x: number; z: number }): PlazaStructure {
  const g = new THREE.Group();
  g.position.set(pos.x, 0, pos.z);
  // SW subroom — face north (toward corridor doorway)
  g.rotation.y = Math.PI;

  const body = new THREE.Mesh(
    new THREE.BoxGeometry(2.2, 3.6, 1.3),
    new THREE.MeshStandardMaterial({
      color: COL_RED, emissive: 0x7f1d1d, emissiveIntensity: 0.2,
      roughness: 0.5,
    }),
  );
  body.position.y = 1.8;
  g.add(body);
  const crown = new THREE.Mesh(
    new THREE.BoxGeometry(2.4, 0.3, 1.4),
    new THREE.MeshStandardMaterial({
      color: COL_WHITE, emissive: COL_WHITE, emissiveIntensity: 0.3,
    }),
  );
  crown.position.y = 3.75;
  g.add(crown);
  const glass = new THREE.Mesh(
    new THREE.BoxGeometry(1.8, 1.8, 0.1),
    new THREE.MeshStandardMaterial({
      color: 0x0ea5e9, emissive: 0x0ea5e9, emissiveIntensity: 0.7,
      transparent: true, opacity: 0.5,
    }),
  );
  glass.position.set(0, 2.4, 0.66);
  g.add(glass);
  const canColors = [0xef4444, 0xf59e0b, 0x22c55e, 0x3b82f6, 0xa855f7];
  const cans: THREE.Mesh[] = [];
  for (let row = 0; row < 3; row++) {
    for (let col = 0; col < 4; col++) {
      const can = new THREE.Mesh(
        new THREE.CylinderGeometry(0.14, 0.14, 0.38, 10),
        new THREE.MeshStandardMaterial({
          color: canColors[(row * 4 + col) % canColors.length],
          emissive: canColors[(row * 4 + col) % canColors.length],
          emissiveIntensity: 0.8,
        }),
      );
      can.position.set(-0.58 + col * 0.4, 3.0 - row * 0.6, 0.5);
      g.add(can);
      cans.push(can);
    }
  }
  const sign = makeTextPanel('SODA', 1.6, 0.5, '#fef3c7', '#991b1b');
  sign.position.set(0, 3.75, 0.71);
  g.add(sign);
  // LED chase strip across the front of the crown — 12 small emissive
  // dots in the warning-colour sequence (red/amber/white). The chase
  // phase updates per frame in update() below; emissive intensity
  // ramps up at the chase head and falls off behind.
  const LED_COUNT = 12;
  const LED_COLORS = [0xef4444, 0xf59e0b, 0xfbbf24, 0xfef3c7];
  const leds: THREE.Mesh[] = [];
  for (let i = 0; i < LED_COUNT; i++) {
    const c = LED_COLORS[i % LED_COLORS.length];
    const led = new THREE.Mesh(
      new THREE.SphereGeometry(0.05, 8, 6),
      new THREE.MeshStandardMaterial({
        color: c, emissive: c, emissiveIntensity: 0.4,
      }),
    );
    // Spread evenly across the crown front face (y=3.75, z=0.72).
    const t = (i + 0.5) / LED_COUNT;
    led.position.set(-1.05 + t * 2.1, 3.62, 0.72);
    g.add(led);
    leds.push(led);
  }
  const ring = makeGlowRing(0xf59e0b);
  g.add(ring);

  parent.add(g);
  return {
    kind: 'gacha', label: 'Soda Machine',
    x: pos.x, z: pos.z, group: g, glowRing: ring,
    interactRadius: INTERACT_RADIUS,
    update(tSec) {
      const idx = Math.floor(tSec * 2) % cans.length;
      for (let i = 0; i < cans.length; i++) {
        const mat = cans[i].material as THREE.MeshStandardMaterial;
        mat.emissiveIntensity = i === idx ? 1.3 : 0.8;
      }
      // LED chase — head position cycles left-to-right at ~3 Hz, brightness
      // falls off exponentially behind the head so it reads as a comet.
      const head = (tSec * LED_COUNT * 0.6) % LED_COUNT;
      for (let i = 0; i < LED_COUNT; i++) {
        const dist = (head - i + LED_COUNT) % LED_COUNT;
        const tail = Math.max(0, 1 - dist / 4);
        const mat = leds[i].material as THREE.MeshStandardMaterial;
        mat.emissiveIntensity = 0.35 + tail * 1.6;
      }
      (ring.material as THREE.MeshBasicMaterial).opacity =
        0.35 + 0.22 * (0.5 + 0.5 * Math.sin(tSec * 2 + 3));
    },
  };
}

function makePatientBoard(parent: THREE.Object3D, pos: { x: number; z: number }): PlazaStructure {
  const g = new THREE.Group();
  g.position.set(pos.x, 0, pos.z);
  // NE subroom — face south (toward corridor doorway)
  g.rotation.y = 0;

  const cork = new THREE.Mesh(
    new THREE.BoxGeometry(4.6, 2.8, 0.25),
    new THREE.MeshStandardMaterial({ color: 0xb08968, roughness: 0.85 }),
  );
  cork.position.y = 2.1;
  g.add(cork);
  const frameMat = new THREE.MeshStandardMaterial({
    color: 0x4b5563, roughness: 0.4, metalness: 0.3,
  });
  for (const [w, h, y, x] of [
    [4.9, 0.2, 3.6, 0],
    [4.9, 0.2, 0.6, 0],
    [0.2, 3.0, 2.1, -2.4],
    [0.2, 3.0, 2.1,  2.4],
  ] as const) {
    const bar = new THREE.Mesh(new THREE.BoxGeometry(w, h, 0.3), frameMat);
    bar.position.set(x, y, 0);
    g.add(bar);
  }
  const cardMat = new THREE.MeshStandardMaterial({ color: COL_WHITE });
  const pinColors = [0xef4444, 0x22c55e, 0xfbbf24, 0x3b82f6];
  for (let row = 0; row < 3; row++) {
    for (let col = 0; col < 4; col++) {
      const card = new THREE.Mesh(new THREE.PlaneGeometry(0.75, 0.75), cardMat);
      card.position.set(-1.55 + col * 0.95, 2.65 - row * 0.8, 0.14);
      card.rotation.z = (Math.random() - 0.5) * 0.15;
      g.add(card);
      const pc = pinColors[(row + col) % 4];
      const pin = new THREE.Mesh(
        new THREE.SphereGeometry(0.07, 8, 6),
        new THREE.MeshStandardMaterial({
          color: pc, emissive: pc, emissiveIntensity: 0.6,
        }),
      );
      pin.position.set(card.position.x, card.position.y + 0.3, 0.2);
      g.add(pin);
    }
  }
  const sign = makeTextPanel('PATIENT STANDINGS', 4.0, 0.6, '#0f172a', '#fbbf24');
  sign.position.set(0, 3.9, 0);
  g.add(sign);
  const signMat = sign.material as THREE.MeshBasicMaterial;
  const signBaseY = sign.position.y;
  const ring = makeGlowRing(0xfbbf24);
  g.add(ring);

  parent.add(g);
  return {
    kind: 'leaderboards', label: 'Patient Standings',
    x: pos.x, z: pos.z, group: g, glowRing: ring,
    interactRadius: INTERACT_RADIUS,
    update(tSec) {
      (ring.material as THREE.MeshBasicMaterial).opacity =
        0.35 + 0.22 * (0.5 + 0.5 * Math.sin(tSec * 2 + 2));
      // CRT flicker on the sign — 90% of the time the sign is at full
      // opacity, occasionally it dips for 80-160 ms (faulty fluorescent
      // tube vibe). Phase randomized per board via the x position so
      // multiple boards in the future wouldn't sync.
      const flickerSeed = tSec * 1.7 + pos.x * 0.41;
      const wobble = Math.sin(flickerSeed * 7.3) + Math.sin(flickerSeed * 13.1);
      // wobble ranges roughly -2..2; clamp to a "mostly bright, sometimes dip" curve.
      const dip = Math.max(0, wobble - 1.4) * 0.6;
      signMat.opacity = 1 - dip;
      // Tiny vertical jitter on dips so the panel feels like a real CRT
      // losing sync for a frame.
      sign.position.y = signBaseY + (dip > 0 ? (Math.random() - 0.5) * 0.018 : 0);
    },
  };
}

function makeMedsCart(parent: THREE.Object3D, pos: { x: number; z: number }): PlazaStructure {
  const g = new THREE.Group();
  g.position.set(pos.x, 0, pos.z);
  // SE subroom — face north (toward corridor doorway)
  g.rotation.y = Math.PI;

  const cartMat = new THREE.MeshStandardMaterial({
    color: COL_STEEL, roughness: 0.3, metalness: 0.7,
  });
  const frame = new THREE.Mesh(new THREE.BoxGeometry(2.2, 0.08, 1.4), cartMat);
  frame.position.y = 1.6;
  g.add(frame);
  const shelf2 = frame.clone(); shelf2.position.y = 1.0; g.add(shelf2);
  const shelf3 = frame.clone(); shelf3.position.y = 0.4; g.add(shelf3);
  for (const [x, z] of [[-1.0, -0.6], [1.0, -0.6], [-1.0, 0.6], [1.0, 0.6]] as const) {
    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, 1.7, 8), cartMat);
    post.position.set(x, 0.85, z);
    g.add(post);
  }
  for (const [x, z] of [[-0.9, -0.6], [0.9, -0.6], [-0.9, 0.6], [0.9, 0.6]] as const) {
    const wheel = new THREE.Mesh(
      new THREE.CylinderGeometry(0.16, 0.16, 0.12, 12),
      new THREE.MeshStandardMaterial({ color: 0x1f2937 }),
    );
    wheel.rotation.z = Math.PI / 2;
    wheel.position.set(x, 0.16, z);
    g.add(wheel);
  }
  const pillColors = [0xf59e0b, 0xf472b6, 0x38bdf8, 0x84cc16, 0xa78bfa];
  const bottles: THREE.Mesh[] = [];
  for (let i = 0; i < 6; i++) {
    const bottleMat = new THREE.MeshStandardMaterial({
      color: pillColors[i % pillColors.length],
      emissive: pillColors[i % pillColors.length],
      emissiveIntensity: 0.5,
      transparent: true, opacity: 0.85,
    });
    const b = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.13, 0.4, 10), bottleMat);
    b.position.set(-0.8 + (i % 3) * 0.55, 1.88, -0.3 + Math.floor(i / 3) * 0.55);
    g.add(b);
    bottles.push(b);
    const cap = new THREE.Mesh(
      new THREE.CylinderGeometry(0.135, 0.135, 0.08, 10),
      new THREE.MeshStandardMaterial({ color: COL_WHITE }),
    );
    cap.position.set(b.position.x, 2.12, b.position.z);
    g.add(cap);
  }
  const sign = makeTextPanel('COMMISSARY', 2.8, 0.7, '#0f172a', '#f472b6');
  sign.position.set(0, 2.8, 0);
  g.add(sign);
  const ring = makeGlowRing(0xf472b6);
  g.add(ring);

  parent.add(g);
  return {
    kind: 'shop', label: 'Commissary',
    x: pos.x, z: pos.z, group: g, glowRing: ring,
    interactRadius: INTERACT_RADIUS,
    update(tSec) {
      for (let i = 0; i < bottles.length; i++) {
        bottles[i].position.y = 1.88 + Math.sin(tSec * 1.8 + i * 0.5) * 0.04;
      }
      (ring.material as THREE.MeshBasicMaterial).opacity =
        0.35 + 0.22 * (0.5 + 0.5 * Math.sin(tSec * 2 + 1));
    },
  };
}

export function nearestPlazaStructure(
  structures: ReadonlyArray<PlazaStructure>,
  x: number,
  z: number,
): PlazaStructure | null {
  let best: PlazaStructure | null = null;
  let bestDist = Infinity;
  for (const s of structures) {
    const d = Math.hypot(s.x - x, s.z - z);
    if (d <= s.interactRadius && d < bestDist) {
      bestDist = d;
      best = s;
    }
  }
  return best;
}
